import React, { forwardRef, memo } from 'react';
import { usePlatformLeafEventHandler } from '@atlaskit/analytics-next';
import Box from '@atlaskit/ds-explorations/box';
import Inline from '@atlaskit/ds-explorations/inline';
import noop from '@atlaskit/ds-lib/noop';
import useControlled from '@atlaskit/ds-lib/use-controlled';
import ChevronLeftLargeIcon from '@atlaskit/icon/glyph/chevron-left-large';
import ChevronRightLargeIcon from '@atlaskit/icon/glyph/chevron-right-large';
import Navigator from './internal/components/navigator';
import PageComponent from './internal/components/page';
import renderDefaultEllipsis from './internal/components/render-ellipsis';
import { emptyObject } from './internal/constants';
import collapseRange from './internal/utils/collapse-range';
const analyticsAttributes = {
  componentName: 'pagination',
  packageName: "@atlaskit/pagination",
  packageVersion: "14.2.8"
};
function InnerPagination({
  components = emptyObject,
  defaultSelectedIndex = 0,
  selectedIndex,
  label = 'pagination',
  previousLabel = 'previous',
  nextLabel = 'next',
  style = emptyObject,
  max = 7,
  onChange = noop,
  pages,
  getPageLabel,
  renderEllipsis = renderDefaultEllipsis,
  analyticsContext,
  testId
}, ref) {
  const [selectedIndexValue, setSelectedIndexValue] = useControlled(selectedIndex, () => defaultSelectedIndex || 0);
  const onChangeWithAnalytics = usePlatformLeafEventHandler({
    fn: (value, analyticsEvent) => {
      const {
        event,
        selectedPageIndex
      } = value;
      if (selectedIndex === undefined) {
        setSelectedIndexValue(selectedPageIndex);
      }
      if (onChange) {
        onChange(event, pages[selectedPageIndex], analyticsEvent);
      }
    },
    action: 'changed',
    actionSubject: 'pageNumber',
    analyticsData: analyticsContext,
    ...analyticsAttributes
  });
  const transform = (page, currPageIndex, testId) => {
    const selectedPage = pages[selectedIndexValue];
    return /*#__PURE__*/React.createElement(PageComponent, {
      key: `page-${getPageLabel ? getPageLabel(page, currPageIndex) : currPageIndex}`,
      component: components.Page,
      onClick: event => onChangeWithAnalytics({
        event,
        selectedPageIndex: currPageIndex
      }),
      isSelected: page === selectedPage,
      page: page,
      testId: testId && `${testId}--${page === selectedPage ? 'current-' : ''}page-${currPageIndex}`
    }, getPageLabel ? getPageLabel(page, currPageIndex) : page);
  };
  return /*#__PURE__*/React.createElement(Box, {
    testId: testId,
    UNSAFE_style: style,
    ref: ref,
    "aria-label": label,
    as: "nav"
  }, /*#__PURE__*/React.createElement(Inline, {
    gap: "space.0",
    alignItems: "center"
  }, /*#__PURE__*/React.createElement(Navigator, {
    key: "left-navigator",
    component: components.Previous,
    onClick: event => onChangeWithAnalytics({
      event,
      selectedPageIndex: selectedIndexValue - 1
    }),
    isDisabled: selectedIndexValue === 0,
    iconBefore: /*#__PURE__*/React.createElement(ChevronLeftLargeIcon, {
      label: ""
    }),
    "aria-label": previousLabel,
    pages: pages,
    testId: testId && `${testId}--left-navigator`
  }), collapseRange(pages, selectedIndexValue, {
    max: max,
    ellipsis: renderEllipsis,
    transform
  }, testId), /*#__PURE__*/React.createElement(Navigator, {
    key: "right-navigator",
    component: components.Next,
    onClick: event => onChangeWithAnalytics({
      event,
      selectedPageIndex: selectedIndexValue + 1
    }),
    isDisabled: selectedIndexValue === pages.length - 1,
    iconBefore: /*#__PURE__*/React.createElement(ChevronRightLargeIcon, {
      label: ""
    }),
    "aria-label": nextLabel,
    pages: pages,
    testId: testId && `${testId}--right-navigator`
  })));
}
const Pagination = /*#__PURE__*/forwardRef(InnerPagination);
export default /*#__PURE__*/memo(Pagination);